import { truncateText } from '@chronosphereio/core';
import { StateCreator } from 'zustand';
import { CopiedPanelSource, PanelDefinition } from '@/core';
import { GridLayoutDimensions } from '@/dashboards/components';
import { EMPTY_PANEL_GROUP_TITLE } from '@/dashboards/constants';
import { findNextAvailablePosition, getUniquePanelKey } from '@/dashboards/utils/panelUtils';
import { generateId, Middleware } from './common';
import { FocusSlice } from './focus-slice';
import { HistorySlice } from './history-slice';
import {
  addPanelGroup,
  createEmptyPanelGroup,
  PanelGroupDefinition,
  PanelGroupId,
  PanelGroupItemLayout,
  PanelGroupSlice,
} from './panel-group-slice';
import { PanelSlice } from './panel-slice';

/**
 * Copied panel is stored in clipboard html as custom data-panel attribute for smart copy-paste functionality.
 */
export interface CopiedPanelData {
  /** Original panel key when copied */
  panelKey: string;
  /** Panel definition from source copied panel */
  panelDefinition: PanelDefinition;
  /**
   * Panel definition with variables interpolated from when the panel was copied.
   * Useful when pasting on a separate dashboard that might not have the same variables defined.
   */
  interpolatedPanelDefinition?: PanelDefinition;
  /** Timestamp when the panel was copied (used for expiration) */
  timestamp: number;
  /** Metadata about where the panel was copied from */
  source: CopiedPanelSource;
  /** Layout dimensions of the original panel (width and height in grid units) */
  layout?: GridLayoutDimensions;
}

export interface PastePanelSlice {
  pastePanel: (copiedPanelData: CopiedPanelData) => void;
}

export function createPastePanelSlice(): StateCreator<
  PastePanelSlice & PanelSlice & PanelGroupSlice & FocusSlice & HistorySlice,
  Middleware,
  [],
  PastePanelSlice
> {
  return (set, get) => ({
    pastePanel: (copiedPanelData: CopiedPanelData) => {
      get().initializeHistoryIfEmpty();
      set(
        (state) => {
          const { panels, panelGroups, panelGroupOrder, focusedPanelGroupId } = state;

          // Prefer interpolated spec if provided
          const panel = copiedPanelData.interpolatedPanelDefinition ?? copiedPanelData.panelDefinition;

          const panelKey = getUniquePanelKey({ panels });

          // Use the stored layout dimensions or fall back to defaults.
          const storedLayout = copiedPanelData.layout;
          const newPanelWidth = storedLayout?.w ?? 12;
          const newPanelHeight = storedLayout?.h ?? 6;

          // Find a target group to paste into. If empty dashboard / no group exists, create one.
          let targetGroup = findTargetPanelGroup(focusedPanelGroupId, panelGroups, panelGroupOrder);
          if (!targetGroup) {
            const newGroup = createEmptyPanelGroup();
            newGroup.title = EMPTY_PANEL_GROUP_TITLE;
            addPanelGroup(state, newGroup);
            targetGroup = state.panelGroups[newGroup.id];
          }

          if (targetGroup?.kind !== 'Grid') {
            throw new Error('No grid panel group available to paste panel into.');
          }

          const { x, y } = findNextAvailablePosition(targetGroup.itemLayouts, newPanelWidth, newPanelHeight);

          const newLayoutItem: PanelGroupItemLayout = {
            i: generateId().toString(),
            x,
            y,
            w: newPanelWidth,
            h: newPanelHeight,
          };

          // Add the new panel from clipboard to panel group grid.
          state.panels[panelKey] = panel;
          targetGroup.itemLayouts.push(newLayoutItem);
          targetGroup.itemPanelKeys[newLayoutItem.i] = panelKey;
        },
        false,
        '[Panel] pastePanel'
      );
      get().pushToHistory({
        actionId: `paste-panel-${generateId()}`,
        actionName: `Pasted panel: ${truncateText(copiedPanelData.panelDefinition?.spec?.display?.name ?? copiedPanelData.panelKey, 20)}`,
      });
    },
  });
}

/**
 * Determines the target panel group for a paste operation, falling back to the last grid group.
 */
function findTargetPanelGroup(
  focusedGroupId: PanelGroupId | undefined,
  panelGroups: Record<PanelGroupId, PanelGroupDefinition>,
  panelGroupOrder: PanelGroupId[]
): PanelGroupDefinition | undefined {
  const targetGroupId = focusedGroupId ?? [...panelGroupOrder].reverse().find((id) => panelGroups[id]?.kind === 'Grid');

  if (targetGroupId === undefined) return undefined;

  const targetGroup = panelGroups[targetGroupId];
  return targetGroup?.kind === 'Grid' ? targetGroup : undefined;
}
